import News from "@/components/News";
import SEO from "@/components/SEO";
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks";
import { seoQuerySchema } from "@/lib/sanity/queries";
import {
  NewsFilter,
  setAllNewsFilters,
} from "@/lib/redux/newsFilters/newsFiltersSlice";
import { newsFilterSelectors } from "@/lib/redux/store";
import { getClient } from "@/lib/sanity/server";
import { isValid } from "date-fns";
import type { GetStaticProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import useSWRInfinite from "swr/infinite";
import { z } from "zod";

/** Number of articles per page */
const PAGE_SIZE = 5;

/**
 * Parameters required by the query
 */
type QueryParams = {
  start: number;
  end: number;
  locale: string;
  defaultLocale: string;
  search: string;
  years: number[];
  categories: string[];
};

/**
 * Validator for search query response
 */
const searchQueryDataSchema = z.object({
  hits: z.array(
    z.object({
      _id: z.string(),
      slug: z.string(),
      releaseDate: z.string(),
      category: z.string().nullable(),
      heading: z.string(),
      excerpt: z.string(),
      thumbnail: z.object({
        asset: z.object({
          url: z.string(),
        }),
      }),
    })
  ),
  total: z.number(),
});

type SearchQueryData = z.infer<typeof searchQueryDataSchema>;

/**
 * Validator for page query response
 */
const pageQueryDataSchema = z.object({
  heading: z.string(),
  seo: seoQuerySchema,
  filters: z.object({
    releaseDate: z.array(z.string()),
    category: z.array(z.string()),
  }),
});

type PageQueryData = z.infer<typeof pageQueryDataSchema>;

/**
 * Function that performs the query
 */
const fetcher = async (params: QueryParams) => {
  const releaseYearFilter =
    params.years
      ?.map((year) => /* groq */ `releaseDate match "${year}-*"`)
      .join(" || ") || true;

  const categoryFilter =
    params.categories
      ?.map((category) => {
        const categoryId = category.split(':')[1]
        return `category._ref == "${categoryId}"`
      })
      .join(" || ") || true;

  const textFilter = params.search
    ? /* groq */ `(
      coalesce(excerpt[$locale], excerpt[$defaultLocale]) match $search + "*" ||
      coalesce(heading[$locale], heading[$defaultLocale]) match $search + "*"
  )`
    : true;

  const articleQuery = /* groq */ `*[_type == "article" && (${releaseYearFilter}) && (${textFilter}) && (${categoryFilter})]`;

  const query = /* groq */ `{
      "hits": ${articleQuery} | order(orderRank desc) [$start..$end]{
        _id,
        "slug": slug.current,
        releaseDate,
        "category": category->{"title": coalesce(title[$locale], title[$defaultLocale])}.title,
        "heading": coalesce(heading[$locale], heading[$defaultLocale]),
        "excerpt": coalesce(excerpt[$locale], excerpt[$defaultLocale]),
        thumbnail {
          asset->{ url }
        }
      },
      "total": count(${articleQuery})
  }`;

  const res = await getClient().fetch(query, params);
  return searchQueryDataSchema.parse(res);
};

type NewsProps = {
  searchData: SearchQueryData;
  pageData: PageQueryData;
};

/**
 * Fetches first page of data for SEO
 * @returns
 */
export const getStaticProps: GetStaticProps<NewsProps> = async ({
  defaultLocale = "it",
  locale = "it",
}) => {
  const searchData = await fetcher({
    start: 0,
    end: PAGE_SIZE,
    defaultLocale,
    locale,
    search: "*",
    years: [],
    categories: [],
  });

  const pageQuery = /* groq */ `*[_type == "newsPage"][0] {
    seo {
      "metaTitle": coalesce(metaTitle[$locale], metaTitle[$defaultLocale]),
      "metaDesc": coalesce(metaDesc[$locale], metaDesc[$defaultLocale]),
      "shareTitle": coalesce(shareTitle[$locale], shareTitle[$defaultLocale]),
      "shareDesc": coalesce(shareDesc[$locale], shareDesc[$defaultLocale]),
      shareGraphic {
        asset->{
          ...,
          "alt": coalesce(alt[$locale], alt[$defaultLocale])
        }
      }
    },
    "heading": coalesce(heading[$locale], heading[$defaultLocale]),
    "filters": {
      "releaseDate": *[_type == "article"].releaseDate,
      "category": *[_type == "article"].category->{"title":coalesce(title[$locale], title[$defaultLocale]), _id},
    }
  }`;
  const pageData = await getClient().fetch(pageQuery, {
    defaultLocale,
    locale,
  });

  return {
    props: {
      searchData,
      pageData,
    },
    revalidate: 60,
  };
};

/**
 *
 * @param props
 * @returns
 */
const NewsPage: NextPage<NewsProps> = (props) => {
  const { searchData, pageData } = props;
  const { locale = "it", defaultLocale = "it" } = useRouter();

  const dispatch = useAppDispatch();

  useEffect(() => {
    const yearFilter: NewsFilter = {
      id: "Year",
      values: [],
      selectedValues: [],
    };

    const categoryFilter: NewsFilter = {
      id: "Category",
      values: [],
      selectedValues: [],
    };

    // Extract unique set of years from article release dates
    const years = new Set<string>();
    pageData.filters.releaseDate.forEach((releaseDate) => {
      const d = new Date(releaseDate);
      if (isValid(d)) years.add(d.getFullYear().toString());
    });

    // Extract unique set of categories from article categories
    const categories = new Set<string>();
    pageData.filters.category?.forEach((category: any) => {
      if (category) categories.add(`${category.title}:${category._id}`)
    });

    // Save filters in redux store
    yearFilter.values = Array.from(years);
    categoryFilter.values = Array.from(categories);
    const filters = [yearFilter, categoryFilter];
    dispatch(setAllNewsFilters(filters));
  }, [pageData, dispatch]);

  const years = useAppSelector(
    (state) =>
      newsFilterSelectors
        .selectById(state, "Year")
        ?.selectedValues?.map((value) => parseInt(value))
        ?.filter((value) => !isNaN(value)) || []
  );
  const search = useAppSelector((state) => state.newsSearch.query || "");
  const categories = useAppSelector(
    (state) =>
      newsFilterSelectors
        .selectById(state, "Category")
        ?.selectedValues?.map((value) => value.trim()) || []
  );

  /** Resolves query params for the current page  */
  const keyLoader = useCallback(
    (index: number): QueryParams => {
      const start = index * PAGE_SIZE;
      const end = start + PAGE_SIZE + 1;

      return {
        start,
        end,
        locale,
        defaultLocale,
        search,
        years,
        categories,
      };
    },
    [locale, defaultLocale, years, search, categories]
  );

  const { data, setSize, mutate } = useSWRInfinite(keyLoader, fetcher, {
    fallbackData: [searchData],
    revalidateOnFocus: false,
    revalidateOnMount: false,
    revalidateOnReconnect: false,
  });

  const isEmpty = data?.[0]?.hits.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.hits.length < PAGE_SIZE);

  // Concatenate hits from every page
  const articles =
    data &&
    ([] as SearchQueryData["hits"]).concat(...data.map((page) => page.hits));

  // Total is calulated across every existing article so the result is the same on every page.
  const total = data && data[0]?.total;

  const loadMore = () => setSize((size) => size + 1);

  return (
    <>
      {pageData.seo && <SEO {...pageData.seo} slug={"news"} />}
      <News
        heading={pageData.heading}
        articles={articles || []}
        total={total || 0}
        isReachingEnd={isReachingEnd}
        loadMore={loadMore}
      />
    </>
  );
};

export default NewsPage;
